import { GridQuery } from '../entities/enemies/BaseEnemy';
import { GRID_W, GRID_H } from '../types';

export type Step = { col: number; row: number };

/**
 * Breadth-first search from (fromCol, fromRow) toward (toCol, toRow).
 * Returns the first step (next tile) on the shortest free path, or null.
 * Treats `grid.isBlocked` cells as walls.
 */
export function bfsNextStep(
  grid: GridQuery,
  fromCol: number, fromRow: number,
  toCol: number, toRow: number,
): Step | null {
  if (fromCol === toCol && fromRow === toRow) return null;

  const visited: boolean[][] = Array.from({ length: GRID_H }, () => Array(GRID_W).fill(false));
  const parent: Array<Array<Step | null>> = Array.from({ length: GRID_H }, () => Array(GRID_W).fill(null));
  const queue: Step[] = [{ col: fromCol, row: fromRow }];
  visited[fromRow][fromCol] = true;

  const dirs: Step[] = [{ col: 1, row: 0 }, { col: -1, row: 0 }, { col: 0, row: 1 }, { col: 0, row: -1 }];
  let found: Step | null = null;

  while (queue.length > 0) {
    const cur = queue.shift()!;
    if (cur.col === toCol && cur.row === toRow) { found = cur; break; }
    for (const d of dirs) {
      const nc = cur.col + d.col;
      const nr = cur.row + d.row;
      if (nc < 0 || nc >= GRID_W || nr < 0 || nr >= GRID_H) continue;
      if (visited[nr][nc]) continue;
      // Allow the destination tile even if "blocked" (so we still target the player)
      const isTarget = nc === toCol && nr === toRow;
      if (!isTarget && grid.isBlocked(nc, nr)) continue;
      visited[nr][nc] = true;
      parent[nr][nc] = cur;
      queue.push({ col: nc, row: nr });
    }
  }

  if (!found) return null;
  // Walk back to first step from origin
  let cur: Step = found;
  let prev: Step | null = parent[cur.row][cur.col];
  while (prev && !(prev.col === fromCol && prev.row === fromRow)) {
    cur = prev;
    prev = parent[cur.row][cur.col];
  }
  return cur;
}
